import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Crown, ArrowRight, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed
  const attemptsRef = useRef(0);
  const { refreshMe } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      if (attemptsRef.current > 20) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshMe();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {
        // ignore, retry
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, refreshMe]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF9F5] px-4">
      <div
        data-testid="payment-success-card"
        className="w-full max-w-md rounded-3xl border border-[#E6E2D8] bg-white p-8 text-center shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]"
      >
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-indigo-600" />
            <h1 className="font-heading text-2xl font-bold text-[#1A1D20]">
              Confirming your payment…
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Hang tight — this usually takes a couple of seconds.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#1A1D20]">
              You're on Pro <Crown className="inline h-5 w-5 text-amber-500" />
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Unlimited rewrites are unlocked on this account. Welcome!
            </p>
            <Button
              data-testid="success-go-to-app"
              onClick={() => navigate("/app")}
              className="mt-6 h-11 w-full gap-2 rounded-full bg-[#1A1D20] font-semibold text-[#FAF9F5] hover:bg-[#2A2E3D]"
            >
              Start writing
              <ArrowRight className="h-4 w-4" />
            </Button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="mx-auto mb-4 h-10 w-10 text-red-500" />
            <h1 className="font-heading text-2xl font-bold text-[#1A1D20]">
              {status === "timeout"
                ? "Still waiting on Stripe…"
                : "We couldn't confirm your payment"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {status === "timeout"
                ? "This can happen if the checkout is still open in another tab. You can refresh, or head back to the pricing page."
                : "Nothing was charged. Please try again from the pricing page."}
            </p>
            <div className="mt-6 flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/pricing")}
                className="h-11 flex-1 rounded-full border-[#E6E2D8]"
              >
                Back to pricing
              </Button>
              <Button
                onClick={() => navigate("/app")}
                className="h-11 flex-1 rounded-full bg-[#1A1D20] font-semibold text-[#FAF9F5] hover:bg-[#2A2E3D]"
              >
                Go to editor
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  useEffect(() => {
    toast("Checkout cancelled");
  }, []);
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF9F5] px-4">
      <div
        data-testid="payment-cancel-card"
        className="w-full max-w-md rounded-3xl border border-[#E6E2D8] bg-white p-8 text-center shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]"
      >
        <XCircle className="mx-auto mb-4 h-10 w-10 text-slate-400" />
        <h1 className="font-heading text-2xl font-bold text-[#1A1D20]">
          Checkout cancelled
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          No charge was made. You can pick up right where you left off.
        </p>
        <div className="mt-6 flex gap-2">
          <Link to="/pricing" className="flex-1">
            <Button
              variant="outline"
              className="h-11 w-full rounded-full border-[#E6E2D8]"
            >
              Back to pricing
            </Button>
          </Link>
          <Link to="/app" className="flex-1">
            <Button className="h-11 w-full rounded-full bg-[#1A1D20] font-semibold text-[#FAF9F5] hover:bg-[#2A2E3D]">
              Go to editor
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
